import { isEmpty, isObject } from '@medplum/core';
import { Bundle, OperationOutcome, Resource } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  ArgumentNode,
  DocumentNode,
  FieldNode,
  IntValueNode,
  Kind,
  OperationDefinitionNode,
  parse,
  print,
  visit,
} from 'graphql';
import stringify from 'json-stable-stringify';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLoadMoreOnScroll from './useLoadMoreOnScroll';
import { useSubscriptionWithLimitHandling } from '../services/subscriptions';
import { isMedplumRateLimitError, MEDPLUM_RATE_LIMIT_ERROR_MESSAGE } from '@/utils/medplumRateLimit';

/**
 * Custom error class for cancelled requests
 */
class CancelledRequestError extends Error {
  constructor(message = 'Request was cancelled') {
    super(message);
    this.name = 'CancelledRequestError';
  }
}

/**
 * Maps GraphQL parameter names to their REST API equivalents for subscription criteria.
 *
 * - GraphQL uses snake_case: address_city, birth_date, given_name
 * - REST uses kebab-case: address-city, birth-date, given-name
 * - Parameters starting with underscore remain unchanged: _sort, _count, _offset
 *
 * @see https://www.medplum.com/docs/graphql/basic-queries
 */
function convertGraphQLToRestParams(graphqlParam: string): string {
  // FHIR standard parameters starting with underscore remain unchanged
  if (graphqlParam.startsWith('_')) {
    return graphqlParam;
  }

  return graphqlParam.replace(/_/g, '-');
}

/**
 * Adds pagination parameters to a GraphQL query
 */
/**
 * Exported so non-hook callers that page the same document (the Carrum CSV export)
 * inject pagination exactly the way the UI does. Baking `_count` into a shared query
 * document instead would collide with this injector and make Medplum reject the whole
 * request -- see the root-_count guard in PatientListPage/graphql.test.ts.
 */
export function processGraphQLQuery(ast: DocumentNode, queryFieldName: string, count: number, offset: number): string {
  try {
    // Create the pagination argument nodes
    const countArg: ArgumentNode = {
      kind: Kind.ARGUMENT,
      name: { kind: Kind.NAME, value: '_count' },
      value: { kind: Kind.INT, value: count.toString() } as IntValueNode,
    };

    const offsetArg: ArgumentNode = {
      kind: Kind.ARGUMENT,
      name: { kind: Kind.NAME, value: '_offset' },
      value: { kind: Kind.INT, value: offset.toString() } as IntValueNode,
    };

    // Add pagination arguments to the ROOT query field only.
    //
    // Matching by field name anywhere in the tree is wrong: a query may root on
    // `AppointmentList` AND nest an aliased `AppointmentList` (the CI queues do,
    // for the related consult). The nested one already carries its own `_count`,
    // so a second injected one makes Medplum reject the entire request with
    // "There can be only one argument named _count" — a 400 the UI shows as an
    // empty tab, with nothing naming the cause.
    const modifiedAst = visit(ast, {
      OperationDefinition: {
        enter(node: OperationDefinitionNode) {
          if (node.operation !== 'query') {
            return node;
          }

          return {
            ...node,
            selectionSet: {
              ...node.selectionSet,
              selections: node.selectionSet.selections.map((selection) =>
                selection.kind === Kind.FIELD && selection.name.value === queryFieldName
                  ? { ...selection, arguments: [...(selection.arguments ?? []), countArg, offsetArg] }
                  : selection
              ),
            },
          };
        },
      },
    });

    return print(modifiedAst);
  } catch (error) {
    throw new Error(`Failed to process GraphQL query: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Extracts the query field name from a GraphQL AST
 * Looks for fields ending with "List" (e.g., "PatientList", "CommunicationList")
 * Only examines top-level query fields, not nested fields
 */
function getQueryFieldName(ast: DocumentNode): string {
  let fieldName: string | null = null;

  visit(ast, {
    OperationDefinition: {
      enter(node: OperationDefinitionNode) {
        // Only look at query operations
        if (node.operation === 'query') {
          // Look at the selection set (top-level fields)
          node.selectionSet?.selections.forEach((selection) => {
            if (selection.kind === 'Field' && selection.name.value.endsWith('List')) {
              fieldName = selection.name.value;
            }
          });
        }
      },
    },
  });

  return fieldName || 'ResourceList';
}

/**
 * Derives the resource type from a query field name
 * e.g., "PatientList" -> "Patient"
 */
function getResourceType(queryFieldName: string): string {
  if (queryFieldName.endsWith('List')) {
    return queryFieldName.slice(0, -4); // Remove "List"
  }
  return queryFieldName;
}

/**
 * Extracts filter parameters from a GraphQL AST to build subscription criteria
 * This ensures subscriptions only receive updates for resources matching the query filters
 */
function extractSubscriptionCriteria(
  ast: DocumentNode,
  queryFieldName: string,
  resourceType: string,
  variables?: Record<string, any>
): string {
  try {
    const params: Record<string, string> = {};

    visit(ast, {
      Field: {
        enter(node: FieldNode) {
          // Extract arguments from the specific query field
          if (node.name.value === queryFieldName) {
            // Extract arguments from the query field
            node.arguments?.forEach((arg) => {
              const graphqlArgName = arg.name.value;

              // Skip pagination parameters for subscription criteria
              if (graphqlArgName === '_count' || graphqlArgName === '_offset') {
                return;
              }

              let argValue: string | undefined;

              if (arg.value.kind === 'StringValue') {
                argValue = arg.value.value;
              } else if (arg.value.kind === 'IntValue') {
                argValue = arg.value.value;
              } else if (arg.value.kind === 'BooleanValue') {
                argValue = arg.value.value.toString();
              } else if (arg.value.kind === 'Variable') {
                // Handle GraphQL variables (e.g., $name)
                if (variables) {
                  const variableName = arg.value.name.value;
                  if (variableName in variables) {
                    argValue = String(variables[variableName]);
                  }
                }
              }

              if (argValue !== undefined) {
                // Map GraphQL parameter name to REST API equivalent
                const restParamName = convertGraphQLToRestParams(graphqlArgName);
                params[restParamName] = argValue;
              }
            });
          }
        },
      },
    });

    // Build the subscription criteria string
    const searchParams = new URLSearchParams(params);
    const criteria = searchParams.toString();

    return criteria ? `${resourceType}?${criteria}` : resourceType;
  } catch (error) {
    throw new Error(
      `Failed to extract subscription criteria from GraphQL query: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Creates a search query string from a record of parameters.
 * @param params The parameters to include in the query string.
 * @returns The search query string.
 */
function mountSearchQueryString(params: Record<string, any>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      qs.append(key, value);
    }
  });
  return qs.toString();
}

/**
 * Creates a single resource query from the original AST for subscription updates
 * Removes variable declarations since they're not needed for single resource queries
 */
function createSingleResourceQuery(
  ast: DocumentNode,
  queryFieldName: string,
  resourceType: string,
  resourceId: string
): string {
  try {
    const modifiedAst = visit(ast, {
      OperationDefinition: {
        enter(node: OperationDefinitionNode) {
          // Remove all variable definitions since we're not using variables in single resource queries
          return {
            ...node,
            variableDefinitions: [],
          };
        },
      },
      Field: {
        enter(node: FieldNode) {
          // Replace the specific query field with single resource field
          if (node.name.value === queryFieldName) {
            return {
              ...node,
              name: { kind: Kind.NAME, value: resourceType },
              arguments: [
                {
                  kind: Kind.ARGUMENT,
                  name: { kind: Kind.NAME, value: 'id' },
                  value: { kind: Kind.STRING, value: resourceId },
                },
              ],
            };
          }
          return node;
        },
      },
    });

    return print(modifiedAst);
  } catch (error) {
    throw new Error(
      `Failed to create single resource query: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

export interface UseLiveGraphQLOptions<T extends Resource = Resource> {
  /**
   * Enable pagination mode. When true, only loads first page initially
   * and provides loadMore function for additional pages.
   * When false (default), loads all data automatically.
   */
  enablePagination?: boolean;
  /**
   * Number of items to load per page. Defaults to 200.
   */
  pageSize?: number;
  /**
   * Page automatically until the cohort is exhausted or this many rows are held.
   * Requires `enablePagination`. Absent leaves today's load-on-scroll behaviour.
   *
   * The cap is what keeps this safe: one wide date range would otherwise turn a
   * working list into a hang. When it bites, `cohortTruncated` goes true so the
   * caller can say so on screen — a client-side filter over a silent prefix of
   * the cohort is the defect this mode exists to remove, and an unannounced cap
   * would just reintroduce it further down the list.
   */
  autoLoadUpTo?: number;
  /**
   * Page mode: fetch the one page at this offset instead of appending, and refetch
   * when it changes. Requires `enablePagination`; `loadMore` is off in this mode.
   */
  offset?: number;
  /**
   * Whether to add new items to the top of the list instead of the bottom.
   * Defaults to false.
   */
  addNewItemToTop?: boolean;
  /**
   * Disable the subscription updates
   */
  pauseLiveUpdates?: boolean;
  /**
   * Optional callback for subscription updates
   */
  onSubscriptionCallback?: (updatedResource: T) => void;
  /**
   * Optional callback for refresh completion
   */
  onRefreshCallback?: () => void;
  /**
   * Optional parameters for creating the Subscription. This is exactly the same as the parameters you would use in the `subscriptionCriteria` when creating a subscription.
   *
   * Required, not optional, for a query filtered through `_filter`: the default
   * criteria copies the query's arguments, and Medplum's subscription matcher
   * fails any parameter that is not a search parameter of the resource type —
   * `_filter` included — so that subscription never fires. The hook refuses such
   * a criteria and warns; pass the scope as plain parameters instead. Keep them
   * coarse (the type, a lower bound): the row builder decides membership, and a
   * bound in the criteria means the event that moves a row OUT of scope never
   * arrives (BAR-2708).
   */
  subscriptionParams?: Record<string, any>;
  /**
   * Optional delay for live updates in seconds. Defaults to 0.
   */
  liveUpdateDelay?: number;
}

/**
 * GraphQL hook that returns a live-updating list of FHIR resources with optional pagination.
 *
 * This hook provides:
 * 1. Custom GraphQL query execution with automatic or manual pagination
 * 2. Can fetch ALL resources by iterating through pages (default) or load incrementally
 * 3. Real-time updates via WebSocket subscriptions
 * 4. Flexible query customization with variables
 * 5. Simple array-based results (no Connection API)
 *
 * Note: This hook is designed for Medplum's GraphQL schema which returns direct arrays,
 * not GraphQL Connection patterns with edges/pageInfo.
 *
 * @param query - The GraphQL query string (pagination parameters added automatically)
 * @param variables - Optional variables for the GraphQL query
 * @param options - Optional configuration for pagination behavior and callbacks
 */
export function useLiveGraphQL<T extends Resource = Resource>(
  query: string,
  variables?: Record<string, any>,
  options?: UseLiveGraphQLOptions<T>
) {
  const medplum = useMedplum();

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<Error | OperationOutcome | undefined>();
  const [hasMore, setHasMore] = useState<boolean>(true);

  // Enables cancelling requests mid-flight
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Re-entry guard and offset for `loadMore`, held in refs because state lags.
   *
   * Two calls in the same tick both read `loadingMore === false` and the same
   * `currentOffset`, so both fetched the identical page and appended it — every
   * timeline row rendered twice, since the page's aggregate loadMore fans out to
   * each query at once (BAR-2564).
   */
  const loadMoreInFlightRef = useRef(false);
  const offsetRef = useRef(0);

  // Extract options with defaults
  const enablePagination = options?.enablePagination ?? false;
  const pageSize = options?.pageSize ?? 200;
  const onSubscriptionCallback = options?.onSubscriptionCallback;
  const onRefreshCallback = options?.onRefreshCallback;
  const addNewItemToTop = options?.addNewItemToTop ?? false;
  const pageOffset = options?.offset;

  // Create a stable key for the query + variables
  const queryKey = useMemo(() => stringify({ query, variables, pageOffset }), [query, variables, pageOffset]);

  // Parse the query once and memoize the result
  const queryAst = useMemo(() => parse(query), [query]);

  // Extract query field name from the parsed AST
  const queryFieldName = useMemo(() => getQueryFieldName(queryAst), [queryAst]);

  const resourceType = useMemo(() => getResourceType(queryFieldName), [queryFieldName]);

  const processError = useCallback((err: Error | OperationOutcome | undefined) => {
    if (err instanceof CancelledRequestError) {
      return;
    } else if (err && typeof err === 'object' && 'resourceType' in err && err.resourceType === 'OperationOutcome') {
      setError(err as OperationOutcome);
    } else if (err instanceof Error) {
      setError(err);
    } else {
      setError(new Error('Unknown error occurred while fetching data'));
    }
  }, []);

  // Common function to fetch a single page of data
  const fetchPage = useCallback(
    async (offset: number, signal?: AbortSignal): Promise<T[]> => {
      const paginatedQuery = processGraphQLQuery(queryAst, queryFieldName, pageSize, offset);
      const response = await medplum.graphql(paginatedQuery, null, variables);

      if (signal?.aborted) {
        throw new CancelledRequestError();
      }

      if (response.errors) {
        const msg = response.errors[0]?.message;
        // eslint-disable-next-line no-console
        console.log(msg);

        if (isMedplumRateLimitError(msg)) {
          throw new Error(MEDPLUM_RATE_LIMIT_ERROR_MESSAGE);
        }
      }

      const resourceList = response.data[queryFieldName];
      if (!resourceList) {
        throw new Error(`No data found for query field: ${queryFieldName}`);
      }

      return Array.isArray(resourceList) ? resourceList : [];
    },
    [queryAst, queryFieldName, pageSize, medplum, variables]
  );

  const fetchData = useCallback(
    async (isRefresh = false, signal?: AbortSignal) => {
      if (isObject(variables) && isEmpty(variables)) {
        setLoading(false);
        setHasMore(false);
        setHasLoadedInitialData(true);
        return;
      }

      try {
        setError(undefined);
        if (isRefresh) {
          setRefreshing(true);
          offsetRef.current = 0;
          setHasMore(true);
        } else {
          setLoading(true);
        }

        if (enablePagination) {
          // Pagination mode: load only the first page, or the requested one in page mode
          const startOffset = pageOffset ?? 0;
          const pageResults = await fetchPage(startOffset, signal);
          if (signal?.aborted) return;

          setData(pageResults as T[]);
          setHasLoadedInitialData(true);
          offsetRef.current = startOffset + pageSize;
          setHasMore(pageResults.length === pageSize);
        } else {
          setData([]);
          let allResults: T[] = [];
          let offset = 0;

          while (true) {
            const pageResults = await fetchPage(offset, signal);
            if (signal?.aborted) return;

            allResults = [...allResults, ...(pageResults as T[])];
            setData(allResults);

            // Mark as having initial data after first page loads
            if (offset === 0) {
              setHasLoadedInitialData(true);
            }

            if (pageResults.length < pageSize) {
              break;
            }

            offset += pageSize;
          }
        }
      } catch (err: any) {
        processError(err);

        // A failed attempt is still a finished attempt. Consumers that AND
        // `hasLoadedInitialData` across several queries — the patient timeline gates its
        // skeleton on five — would otherwise wait forever on a query that is never going
        // to answer, showing a loading state with nothing naming the cause. Access policy
        // is the common trigger: Medplum answers a resource type the caller cannot read
        // with `errors: [Forbidden]` and a null list, which `fetchPage` turns into a
        // throw. The query's own `error` is still set, so a consumer that surfaces it
        // still can; one that ignores it degrades to rendering without that data.
        //
        // A cancelled request is excluded: it was superseded, and the replacement fetch
        // owns the flag.
        if (!signal?.aborted && !(err instanceof CancelledRequestError)) {
          setHasLoadedInitialData(true);
          // Nor does it have a next page: leaving hasMore=true keeps the scroll sentinel
          // re-firing loadMore against a query the policy refuses (BAR-2585).
          setHasMore(false);
        }
      } finally {
        if (!signal?.aborted) {
          if (isRefresh) {
            setRefreshing(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [fetchPage, processError, enablePagination, pageSize, variables, pageOffset]
  );

  const refresh = useCallback(() => {
    // Cancel any ongoing request before refreshing
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new abort controller for the refresh
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    fetchData(true, abortController.signal);
    onRefreshCallback?.();
  }, [fetchData, onRefreshCallback]);

  useEffect(() => {
    setHasLoadedInitialData(false);

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    fetchData(false, abortController.signal);

    return () => {
      // Cleanup function to cancel the request if component unmounts or dependencies change
      abortController.abort();
    };
  }, [queryKey, fetchData]);

  const loadMore = useCallback(async () => {
    if (!enablePagination || pageOffset !== undefined || loadMoreInFlightRef.current || !hasMore) {
      return;
    }

    loadMoreInFlightRef.current = true;
    try {
      setLoadingMore(true);
      setError(undefined);

      const pageResults = await fetchPage(offsetRef.current);

      // Append, skipping anything already held. Offsets drift when a live update
      // inserts a row mid-session, so the next page overlaps what is loaded and a
      // plain concat renders the same resource twice — two identical chart notes or
      // call cards until a refresh replaced the page (BAR-2564).
      setData((prevData) => {
        const held = new Set(prevData.map((resource) => resource.id));
        return [...prevData, ...(pageResults as T[]).filter((resource) => !held.has(resource.id))];
      });
      offsetRef.current += pageSize;
      setHasMore(pageResults.length === pageSize);
    } catch (err: any) {
      processError(err);
      setHasMore(false);
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [enablePagination, pageOffset, hasMore, pageSize, fetchPage, processError]);

  // Default scroll handler for pagination
  const handleScroll = useLoadMoreOnScroll({ hasMore, loadingMore, loading, loadMore });

  // Unified scroll pagination object
  const scrollPagination = enablePagination
    ? {
        handleScroll,
        tableContainerProps: {
          style: {
            flex: 1,
            overflow: 'auto' as const,
          },
          onScroll: handleScroll,
        },
      }
    : undefined;

  const autoLoadUpTo = options?.autoLoadUpTo;
  const reachedCap = autoLoadUpTo !== undefined && data.length >= autoLoadUpTo;

  // Drives the existing `loadMore` rather than re-implementing paging, so this
  // mode inherits its re-entry guard and its id de-dup — a live update shifts the
  // offset window, and a plain concat renders the same row twice (BAR-2564).
  //
  // Self-driving: each resolved page clears `loadingMore` and grows `data`, which
  // re-runs this effect for the next page. It settles when the cohort runs out
  // (`hasMore` false), the cap is reached, or a page fails — `loadMore` sets
  // `hasMore` false on error, so a rate-limited page ends the loop instead of
  // spinning on it.
  useEffect(() => {
    if (autoLoadUpTo === undefined || !enablePagination) {
      return;
    }

    if (loading || loadingMore || !hasMore || reachedCap) {
      return;
    }

    loadMore();
  }, [autoLoadUpTo, enablePagination, loading, loadingMore, hasMore, reachedCap, loadMore]);

  // Exhausted the cohort. Only meaningful in cohort mode: under load-on-scroll,
  // `hasMore` going false means the reader scrolled to the bottom, not that the
  // filters above are now describing everything.
  const cohortComplete = autoLoadUpTo !== undefined && !hasMore;
  const cohortTruncated = reachedCap && hasMore;

  const updateDataWithResource = useCallback(
    (updatedResource: T) => {
      setData((prevData) => {
        const existingIndex = prevData.findIndex((resource) => resource.id === updatedResource.id);

        if (existingIndex !== -1) {
          // Replace existing entry (update)
          const updatedData = [...prevData];
          updatedData[existingIndex] = updatedResource;
          return updatedData;
        }

        return addNewItemToTop ? [updatedResource, ...prevData] : [...prevData, updatedResource];
      });

      onSubscriptionCallback?.(updatedResource);
    },
    [onSubscriptionCallback, addNewItemToTop]
  );

  const handleSubscriptionUpdate = useCallback(
    async (bundle: Bundle) => {
      if (options?.liveUpdateDelay) {
        await new Promise((resolve) => setTimeout(resolve, options.liveUpdateDelay! * 1000));
      }

      const updatedResource = bundle.entry?.[1]?.resource as T | undefined;

      if (!updatedResource || !updatedResource.id) {
        return;
      }

      // Fetch the updated resource using the same field selection as the original query
      const singleResourceQuery = createSingleResourceQuery(queryAst, queryFieldName, resourceType, updatedResource.id);

      // `useSubscription` fires this callback and discards the promise, so a
      // thrown 429/5xx here is an unhandled rejection and the merge silently
      // stops. Log and keep the list as it was; the next event or reload
      // catches up.
      let response: { data?: Record<string, unknown>; errors?: Array<{ message?: string }> };
      try {
        response = await medplum.graphql(singleResourceQuery);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(`GraphQL subscription update failed: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }

      if (response.errors) {
        const errorMessages = response.errors.map((e) => e.message?.toLowerCase() ?? '');

        // Check if the error is due to fetching a deleted resource
        const isDeletedResourceError = errorMessages.some(
          (msg: string) => msg.includes('gone') || msg.includes('not found') || msg.includes('404')
        );

        if (isDeletedResourceError) {
          // Resource was deleted on the server — remove from local state
          setData((prev) => prev.filter((r) => r.id !== updatedResource.id));
          return;
        }

        // eslint-disable-next-line no-console
        console.log(`GraphQL subscription update failed: ${response.errors[0].message}`);
      }

      // An error response may carry no `data` at all.
      const fetchedResource = response.data?.[resourceType];
      if (fetchedResource) {
        updateDataWithResource(fetchedResource as T);
      }
    },
    [queryAst, queryFieldName, resourceType, medplum, updateDataWithResource, options?.liveUpdateDelay]
  );

  // Create subscription criteria from the parsed AST
  const subscriptionCriteria = useMemo(() => {
    void queryKey;
    if (options?.pauseLiveUpdates) return '';

    const criteria = options?.subscriptionParams
      ? `${resourceType}?${mountSearchQueryString(options.subscriptionParams)}`
      : extractSubscriptionCriteria(queryAst, queryFieldName, resourceType, variables);

    // Medplum's subscription matcher looks every criteria parameter up as a
    // search parameter of the resource type and fails the match when there is
    // none — `_filter` included, silently, for every resource. Copying a
    // `_filter` argument into the criteria therefore buys a subscription that
    // never fires (BAR-2708). Refuse it loudly instead: the list behaves exactly
    // as it did, and the console names the query that needs `subscriptionParams`.
    if (/(^|[?&])_filter=/.test(criteria)) {
      // eslint-disable-next-line no-console
      console.warn(
        `useLiveGraphQL: not subscribing to ${queryFieldName} — its criteria carries _filter, which Medplum's subscription matcher cannot evaluate. Pass subscriptionParams.`
      );
      return '';
    }

    return criteria;
  }, [
    queryKey,
    options?.pauseLiveUpdates,
    queryAst,
    queryFieldName,
    resourceType,
    variables,
    options?.subscriptionParams,
  ]);

  useSubscriptionWithLimitHandling(subscriptionCriteria, handleSubscriptionUpdate);

  return {
    data,
    loading,
    hasLoadedInitialData,
    refreshing,
    error,
    refresh,
    // Pagination-specific properties
    loadMore,
    loadingMore,
    hasMore,
    scrollPagination,
    // Cohort-mode status. Both false in load-on-scroll mode.
    cohortComplete,
    cohortTruncated,
  } as const;
}
